/**
 * The classification selects — a Library item's Sensitivity and Export Control
 * pickers, drawn by the document edit form (doc.$docId.tsx,
 * DocumentClassificationCard) and the folder settings (FolderSettingsDialog).
 *
 * Library LB6-T8 review R3. Each surface used to type its own
 * `<SelectItem value="cui">CUI</SelectItem>` list. The values are the
 * classification vocabulary in @argusai/shared (`SENSITIVITIES`,
 * `EXPORT_CONTROLS` — the alphabet the api's Zod schemas validate against), so
 * the options are read from there, and so are the labels (`SENSITIVITY_LABELS`,
 * `EXPORT_CONTROL_LABELS` — the one label table, keyed by the alphabet's type,
 * which the api's audit summaries also read; LB6cT2-REV-03).
 *
 * A stored value outside the vocabulary (a row older than the alphabet) is
 * still offered, under its own name, so the select never opens blank on it.
 *
 * Whether a select is DRAWN is not decided here: that is the tenant's
 * compliance frameworks (use-classification-policy.ts, the LB6-T7 rule), and
 * each caller asks it.
 */
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  EXPORT_CONTROLS,
  SENSITIVITIES,
  exportControlLabel,
  sensitivityLabel,
} from '@argusai/shared';

/**
 * The options a select offers: the vocabulary, in its order, plus the current
 * value when it is not in it. Exported for the test.
 */
export function classificationOptions(
  vocabulary: readonly string[],
  current: string,
  label: (value: string) => string,
): Array<{ value: string; label: string }> {
  const values = vocabulary.includes(current) || current === '' ? [...vocabulary] : [...vocabulary, current];
  return values.map((value) => ({ value, label: label(value) }));
}

export function SensitivitySelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Sensitivity</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          {classificationOptions(SENSITIVITIES, value, sensitivityLabel).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ExportControlSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium">Export Control</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          {classificationOptions(EXPORT_CONTROLS, value, exportControlLabel).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
